// Answering an auditor's evidence request: the client picks the document versions that answer it
// (any evidence on file, the platform's register mirrors included) and / or writes a note. The API
// suggests the newest version of every document already filed under the request's control (backend
// lib/evidenceTrail.ts evidenceSuggestionsFor); they are listed first and the newest starts ticked.
// What is sent is recorded once and then fixed (backend lib/evidenceRequests.ts) — more evidence is
// a new request — so the form says so before it is sent.

import { useEffect, useMemo, useState } from "react";
import { theme, btnPrimary, inputStyle } from "../theme";
import { api, ApiError } from "../api/client";
import { fmtDate } from "../lib/format";
import { EvidenceSourceBadge } from "./EvidenceImportPanel";

/** A document the API suggests for a request: the newest version filed under its control. */
export interface SuggestedEvidenceDocument {
  documentId: string;
  documentGroupId: string;
  fileName: string;
  version: number;
  uploadedAt: string;
  source: string;
}

export interface AnswerDocument { documentId: string | null; fileName: string; version: number; source: string; attachedAt: string; newerVersion?: number | null }

interface DocRow { id: string; fileName: string; version: number; source: string; uploadedAt: string }

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.status === 403 ? "Your role does not permit this." : e.message) : fallback;

/** What a fulfilled request was answered with. */
export function EvidenceRequestAnswerSummary({ documents, note, fulfilledAt }: { documents?: AnswerDocument[]; note?: string | null; fulfilledAt?: string | null }) {
  if (!documents?.length && !note) return null;
  return (
    <div style={{ borderTop: "1px solid var(--c-line)", marginTop: 8, paddingTop: 6, fontSize: 12, color: theme.color.text }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 3 }}>
        Answered{fulfilledAt ? ` on ${fmtDate(fulfilledAt)}` : ""} with
      </div>
      {documents?.map((d, i) => (
        <div key={`${d.documentId ?? "gone"}-${i}`} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.fileName}</span>
          <span style={{ fontSize: 11, color: "var(--c-ref)", fontVariantNumeric: "tabular-nums" }}>v{d.version}</span>
          {d.documentId == null && <span style={{ fontSize: 11, color: theme.color.subtle }}>(since deleted)</span>}
          {d.newerVersion != null && <span style={{ fontSize: 11, color: theme.color.warningText }}>v{d.newerVersion} filed since</span>}
        </div>
      ))}
      {note && <div style={{ whiteSpace: "pre-wrap", marginTop: 3, color: theme.color.muted }}>{note}</div>}
    </div>
  );
}

/** The answer form. `scope` carries `organizationId` in the consultant's client view. */
export function EvidenceRequestAnswerForm<T>({ requestId, scope, suggestions = [], onAnswered, onCancel }: {
  requestId: string;
  scope?: Record<string, string | undefined>;
  /** Documents already filed under the request's control, newest first. */
  suggestions?: SuggestedEvidenceDocument[];
  onAnswered: (updated: T) => void;
  onCancel: () => void;
}) {
  const [docs, setDocs] = useState<DocRow[] | null>(null);
  // The newest document filed under the control is the likeliest answer; it starts ticked.
  const [selected, setSelected] = useState<string[]>(() => suggestions.slice(0, 1).map((s) => s.documentId));
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<{ documents: DocRow[] }>("/documents", scope)
      .then((d) => setDocs(d.documents ?? []))
      .catch(() => setDocs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const suggestedIds = useMemo(() => new Set(suggestions.map((s) => s.documentId)), [suggestions]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (docs ?? []).filter((d) => !suggestedIds.has(d.id) && (!q || d.fileName.toLowerCase().includes(q))).slice(0, 50);
  }, [docs, search, suggestedIds]);

  const toggle = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  async function send() {
    // An answer is on record for the auditor and cannot be changed: say what goes before it goes,
    // since the suggested document starts ticked.
    const names = selected.map((id) => suggestions.find((x) => x.documentId === id)?.fileName ?? docs?.find((d) => d.id === id)?.fileName ?? "a document");
    const what = names.length ? `

Documents: ${names.join(", ")}` : "";
    if (!window.confirm(`Send this answer to the auditor? Once sent it cannot be changed.${what}`)) return;
    setBusy(true); setError("");
    try {
      const updated = await api.patch<T>(`/findings/evidence-requests/${requestId}`, { status: "fulfilled", documentIds: selected, responseNote: note.trim() || null }, scope);
      onAnswered(updated);
    } catch (e) {
      setError(errorMessage(e, "Could not send the answer. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const canSend = !busy && (selected.length > 0 || note.trim().length > 0);
  return (
    <div style={{ borderTop: "1px solid var(--c-line)", marginTop: 8, paddingTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      {suggestions.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, marginBottom: 4 }}>Already on file for this control</div>
          {suggestions.map((s) => (
            <label key={s.documentId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={selected.includes(s.documentId)} onChange={() => toggle(s.documentId)} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.fileName}</span>
              <EvidenceSourceBadge source={s.source} />
              <span style={{ fontSize: 11, color: "var(--c-ref)", fontVariantNumeric: "tabular-nums" }}>v{s.version}</span>
              <span style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>{fmtDate(s.uploadedAt)}</span>
            </label>
          ))}
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, margin: "8px 0 0" }}>Or any other document on file</div>
        </div>
      )}
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a document on file" style={{ ...inputStyle, fontSize: 12 }} aria-label="Find a document" />
      <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid var(--c-line)", borderRadius: 3 }}>
        {docs == null && <div style={{ padding: 8, fontSize: 12, color: theme.color.subtle }}>Loading documents…</div>}
        {docs != null && shown.length === 0 && <div style={{ padding: 8, fontSize: 12, color: theme.color.subtle }}>No document matches. Upload it to Documents first, or answer with a note.</div>}
        {shown.map((d) => (
          <label key={d.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 8px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid var(--c-line)" }}>
            <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} />
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.fileName}</span>
            <span style={{ fontSize: 11, color: "var(--c-ref)", fontVariantNumeric: "tabular-nums" }}>v{d.version}</span>
            <span style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>{fmtDate(d.uploadedAt)}</span>
          </label>
        ))}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} rows={2} placeholder="Note for the auditor (optional when a document is attached)" style={{ ...inputStyle, fontSize: 12, resize: "vertical" }} />
      <div style={{ fontSize: 11, color: theme.color.subtle }}>
        The auditor sees exactly the versions you send. Once sent the answer is on record and cannot be changed; anything more needs a new request.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={send} disabled={!canSend} style={{ ...btnPrimary, fontSize: 12, padding: "5px 12px", opacity: canSend ? 1 : 0.6 }}>
          {busy ? "Sending…" : `Send answer${selected.length ? ` (${selected.length} document${selected.length === 1 ? "" : "s"})` : ""}`}
        </button>
        <button onClick={onCancel} disabled={busy} style={{ background: "none", border: "none", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
        {error && <span style={{ fontSize: 11, color: theme.color.dangerText }}>{error}</span>}
      </div>
    </div>
  );
}
